import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/components/Util/reactResponsiveDimensions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { AudioPlayer } from '../AudioPlayer';
import { SpeakingHeader } from './SpeakingHeader';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logError } from '@/utils/logHandling';
import { useExerciseRecord } from '@/hooks/useExerciseRecord';

const losungMap = new Map();

interface TextBodyProps {
  id: string;
  questions: {
    questionAudio?: string;
    openQuestionAnswer?: string;
    questionText?: string;
  }[];
  questionIndex: number;
  isMiniPlayer?: boolean;
  playbackRate?: number;
}

export function TextBody({
  id,
  questions,
  questionIndex,
  isMiniPlayer = false,
  playbackRate = 1.0,
}: TextBodyProps) {
  const responsiveIconSize = useResponsiveFontSize(1.5);
  const responsiveStyles = StyleSheet.create({
    wrapper: {
      width: '100%',
      height: '100%',
      flexDirection: 'row',
      alignItems: 'center',
    },
    container: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      height: '100%',
      width: '100%',
    },
    textSize: {
      fontSize: useResponsiveFontSize(2),
      marginRight: useResponsiveHeight(1),
      textAlign: 'center',
    },
    audioBtns: {
      marginVertical: useResponsiveHeight(1),
      flexDirection: 'row',
      alignItems: 'center',
    },
    btnTextSize: {
      fontSize: useResponsiveFontSize(2),
      marginLeft: useResponsiveWidth(1),
    },
    btn: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: useResponsiveHeight(1.5),
      paddingHorizontal: useResponsiveWidth(1),
      backgroundColor: colors.white,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.23,
      shadowRadius: 2.62,
      elevation: 4,
    },
    disabled: {
      opacity: 0.4,
    },
    solutionContainer: {
      paddingVertical: useResponsiveHeight(1.5),
      paddingHorizontal: useResponsiveWidth(1),
      backgroundColor: colors.lightGrey,
    },
    audioContainer: {
      width: useResponsiveFontSize(5.5),
      aspectRatio: 1,
    },
  });
  const uniqueKey = id + questionIndex;

  const [isSolVisible, setisSolVisible] = useState(false);
  const [isPlayerDisabled, setIsPlayerDisabled] = useState(false);
  const [uriExists, setUriExists] = useState(false);
  const audioPlayerRef = useRef(null);
  const { questionAudio, openQuestionAnswer, questionText } =
    questions?.[questionIndex] ?? {};

  const { isRecording } = useExerciseRecord(questionIndex, 15000, uniqueKey);

  const handleLosungPress = useCallback(() => {
    losungMap.set(uniqueKey, true);
    setisSolVisible(true);
  }, []);

  const handleIsRecording = useCallback((currentlyRecording: boolean) => {
    if (currentlyRecording) {
      if (isMiniPlayer) {
        audioPlayerRef?.current?.stopPlayer?.();
      } else {
        audioPlayerRef?.current?.pausePlayer?.();
      }
      setIsPlayerDisabled(true);
    }
    if (currentlyRecording && !uriExists) setUriExists(true);
  }, []);

  const checkIfRecordExists = async () => {
    try {
      const recordedUri = await AsyncStorage.getItem(`@recording:${uniqueKey}`);
      if (recordedUri) {
        setUriExists(true);
      }
    } catch (error) {
      logError('record does not exist ', error);
    }
  };

  useEffect(() => {
    setisSolVisible(!!losungMap.get(uniqueKey));
    setUriExists(false);
    checkIfRecordExists();
    setIsPlayerDisabled(false);
  }, [uniqueKey]);

  return (
    <View style={responsiveStyles.wrapper}>
      <View style={responsiveStyles.container}>
        {isMiniPlayer ? (
          <View style={responsiveStyles.audioContainer}>
            <AudioPlayer
              ref={audioPlayerRef}
              isDisabled={isPlayerDisabled || isRecording}
              source={questionAudio}
              isMiniPlayer
              playbackRate={playbackRate}
            />
          </View>
        ) : (
          <AudioPlayer
            ref={audioPlayerRef}
            isDisabled={isPlayerDisabled || isRecording}
            source={questionAudio}
            autoPlay
            playbackRate={playbackRate}
          />
        )}
        {questionText && (
          <Text style={responsiveStyles.textSize}>{questionText}</Text>
        )}
        <View style={responsiveStyles.audioBtns}>
          <Text style={responsiveStyles.textSize}>Ihre Antwort:</Text>
          <SpeakingHeader
            uniqueKey={uniqueKey}
            timeoutSeconds={15000}
            currentExercise={questionIndex}
            handleIsRecording={handleIsRecording}
            handleIsAudioPlaying={(isPlaying: boolean) => {
              if (!isPlaying) {
                setIsPlayerDisabled(false);
                return;
              } else setIsPlayerDisabled(true);
              audioPlayerRef?.current?.pausePlayer?.();
            }}
            selectedPerformance={'none'}
          />
        </View>
        {isSolVisible ? (
          <View style={responsiveStyles.solutionContainer}>
            <Text style={responsiveStyles.btnTextSize}>
              Lösung: {openQuestionAnswer}
            </Text>
          </View>
        ) : (
          <PressableNeolexon
            onPress={handleLosungPress}
            style={[
              responsiveStyles.btn,
              (!uriExists || isRecording) && responsiveStyles.disabled,
            ]}
            disabled={!uriExists || isRecording}
          >
            <FontAwesome5
              name="eye"
              size={responsiveIconSize}
              color={colors.primary}
            />
            <Text style={responsiveStyles.btnTextSize}>Lösung</Text>
          </PressableNeolexon>
        )}
      </View>
    </View>
  );
}
